<template>
    <ModalIndex
        v-model:visible="showMergeModal"
        :eventToCloseFunction="clearMergeRef"
        footerClass="justify-end"
        title="Merge into existing file"
        width="max-w-3xl"
    >
        <DsAlertBanner
            v-if="mergeError"
            class="mb-4"
            :title="mergeError"
            variant="error"
        />

        <!-- EMD Summary -->
        <DsCard class="mb-6">
            <h4 class="mb-2 text-sm font-semibold text-ds-primary-500">Early deposit</h4>
            <div class="grid grid-cols-2 gap-y-1 text-sm">
                <span class="text-ds-primary-500">Property address</span>
                <span>{{ mergeEmdRef?.address }}</span>
                <span class="text-ds-primary-500">Deposit amount</span>
                <span>{{ formatMoney((mergeEmdRef?.deposit_amount ?? 0) / 100) }}</span>
                <span class="text-ds-primary-500">Clients</span>
                <span>{{ mergeEmdRef?.clients.map(c => c.email).join(', ') }}</span>
            </div>
        </DsCard>

        <!-- Suggested Files -->
        <div
            v-if="suggestedFiles.length > 0 && !selectedFile"
            class="mb-6"
        >
            <h4 class="mb-2 text-sm font-semibold">Suggested files</h4>
            <div class="flex flex-col gap-2">
                <button
                    v-for="file in suggestedFiles"
                    :key="file.id"
                    class="w-full text-left"
                    type="button"
                    @click="selectFile(file)"
                >
                    <DsCard
                        clickable
                        variant="interactive"
                    >
                        <div class="flex items-center justify-between gap-4">
                            <div>
                                <div class="text-sm font-medium">{{ file.ref_number }}</div>
                                <div class="text-xs text-ds-primary-500">{{ file.address }}</div>
                            </div>
                            <DsBadge
                                :text="file.reason"
                                variant="active"
                            />
                        </div>
                    </DsCard>
                </button>
            </div>
        </div>

        <!-- Search / Selected File -->
        <div class="mb-4">
            <h4 class="mb-2 text-sm font-semibold">
                {{ selectedFile ? 'Selected file' : 'Search by file number' }}
            </h4>

            <!-- Selected file display -->
            <DsCard
                v-if="selectedFile"
                bg-color="bg-ds-gray-100"
            >
                <div class="flex items-center justify-between gap-4">
                    <div>
                        <div class="text-sm font-medium">{{ selectedFile.ref_number }}</div>
                        <div class="text-sm text-ds-primary-500">{{ selectedFile.address }}</div>
                    </div>
                    <DsButton
                        class="no-underline hover:underline"
                        size="sm"
                        variant="link-button"
                        @click="clearSelection"
                    >
                        Change
                    </DsButton>
                </div>
            </DsCard>

            <!-- Search input -->
            <div v-else>
                <DsTextInput
                    v-model.trim="fileSearchTerm"
                    class="mb-2"
                    placeholder="Enter file number..."
                    type="text"
                >
                    <template #leading>
                        <DsIcon
                            icon="fa-magnifying-glass"
                            size="md"
                        />
                    </template>
                </DsTextInput>

                <div
                    v-if="isSearching"
                    class="py-2 text-center text-sm text-ds-primary-500"
                >
                    Searching...
                </div>
                <div
                    v-else-if="fileSearchTerm && searchResults.length === 0 && !isSearching"
                    class="py-2 text-center text-sm text-ds-primary-500"
                >
                    No files found matching "{{ fileSearchTerm }}"
                </div>
                <div
                    v-else-if="searchResults.length > 0"
                    class="flex max-h-48 flex-col gap-1 overflow-y-auto"
                >
                    <button
                        v-for="file in searchResults"
                        :key="file.id"
                        class="w-full text-left"
                        type="button"
                        @click="selectFile(file)"
                    >
                        <DsCard
                            clickable
                            variant="interactive"
                        >
                            <div class="text-sm font-medium">{{ file.ref_number }}</div>
                            <div class="text-xs text-ds-primary-500">{{ file.address }}</div>
                        </DsCard>
                    </button>
                </div>
            </div>
        </div>

        <template #footer>
            <DsButton
                :disabled="isMerging"
                size="md"
                variant="secondary"
                @click="clearMergeRef"
            >
                Cancel
            </DsButton>
            <DsButton
                :disabled="!selectedFile || isMerging"
                :loading="isMerging"
                size="md"
                variant="primary"
                @click="confirmMerge"
            >
                Merge
            </DsButton>
        </template>
    </ModalIndex>
</template>

<script setup lang="ts">
import type { EarlyDeposit, MergeTargetFile, SuggestedMergeTargetFile } from '@/api/interfaces/early-deposit.types';
import DsAlertBanner from '@ds/alert-banner/DsAlertBanner.vue';
import DsBadge from '@/components/design-system/badge/DsBadge.vue';
import DsButton from '@/components/design-system/button/DsButton.vue';
import DsCard from '@/components/design-system/DsCard.vue';
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsTextInput from '@/components/design-system/form/input/text/DsTextInput.vue';
import ModalIndex from '@/components/Components/Modal/ModalIndex.vue';
import { useMergePreFile } from '@/services/early-deposit.service';
import { formatMoney } from '@/shared/utils/format-number';
import { refDebounced } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

const emit = defineEmits<{
    emdMerged: [emdId: EarlyDeposit['id']];
}>();

const { mergeEmdRef, clearMergeRef, fetchSuggestedFilesForMerge, searchFiles, mergeIntoFile } = useMergePreFile();

const showMergeModal = computed<boolean>({
    get: () => !!mergeEmdRef.value,
    set: flag => {
        if (!flag) {
            clearMergeRef();
        }
    },
});

const selectedFile = ref<MergeTargetFile | SuggestedMergeTargetFile>();
const fileSearchTerm = ref('');
const debouncedSearchTerm = refDebounced(fileSearchTerm, 300);
const mergeError = ref('');
const isMerging = ref(false);
const suggestedFiles = ref<SuggestedMergeTargetFile[]>([]);
const searchResults = ref<MergeTargetFile[]>([]);
const isSearching = ref(false);

// Fetch suggested files when modal opens
watch(mergeEmdRef, async emd => {
    suggestedFiles.value = [];
    searchResults.value = [];
    fileSearchTerm.value = '';
    selectedFile.value = undefined;
    mergeError.value = '';

    if (!emd) return;

    suggestedFiles.value = await fetchSuggestedFilesForMerge(emd.id);
});

// Debounced file search
watch(debouncedSearchTerm, async term => {
    if (!term || !mergeEmdRef.value) {
        searchResults.value = [];
        return;
    }

    isSearching.value = true;
    searchResults.value = await searchFiles(mergeEmdRef.value.id, term);
    isSearching.value = false;
});

const selectFile = (file: MergeTargetFile | SuggestedMergeTargetFile) => {
    selectedFile.value = file;
    searchResults.value = [];
    fileSearchTerm.value = '';
};

const clearSelection = () => {
    selectedFile.value = undefined;
};

const confirmMerge = async () => {
    if (!selectedFile.value || !mergeEmdRef.value) return;

    isMerging.value = true;
    mergeError.value = '';

    const { ok, message } = await mergeIntoFile(mergeEmdRef.value.id, selectedFile.value.id);

    isMerging.value = false;

    if (!ok) {
        mergeError.value = message || 'An error occurred while merging. Please try again.';
        return;
    }

    emit('emdMerged', mergeEmdRef.value.id);
    clearMergeRef();
};
</script>
